import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/Login";
import Hub from "@/pages/Hub";
import AdminUsers from "@/pages/AdminUsers";
const BDModule = lazy(() => import("@/modules/bd/App"));
const PortfolioModule = lazy(() => import("@/modules/portfolio/App"));
const HRMSModule = lazy(() => import("@/modules/hrms/App"));
const CRMModule = lazy(() => import("@/modules/crm/App"));
const PartnersModule = lazy(() => import("@partners/App"));
const FormulationsModule = lazy(() => import("@formulations/App"));
const DossiersModule = lazy(() => import("@dossiers/App"));
const ClinicalIntelligenceModule = lazy(() => import("@clinical-intelligence/App"));

// ---------------------------------------------------------------------------
// Legacy redirects
// ---------------------------------------------------------------------------
// (a) Full-slug redirects — old preview URLs (/formulation-master/*, etc.)
const LEGACY_REDIRECTS = {
  "formulation-master": "/formulations",
  "dossier-assistant": "/dossiers",
  "chandan-partners": "/partners",
};

// (b) Bare-path redirects — common standalone-era URLs. E.g. a user with a
// bookmark of the pre-merge Formulations app would type `/molecules/m1`
// expecting to land on the molecule detail. Without this map the URL falls
// through to the Unifier shell and silently renders the Hub, which is what
// the user reported. Mapped to the sub-app that first shipped the slug.
const BARE_LEGACY_REDIRECTS = {
  share: "/formulations/share",
  "share-project": "/formulations/share-project",
  // Formulations (originally at chandan-formulation-master.preview...)
  molecules: "/formulations/molecules",
  projects: "/formulations/projects",
  "formulation-master": "/formulations/formulation-master",
  recommender: "/formulations/recommender",
  inbox: "/formulations/inbox",
  uploads: "/formulations/uploads",
  portfolio: "/formulations/portfolio",
  dashboard: "/formulations",
  "rfq-form": "/partners/rfq-form",
  // Partners bare paths
  suppliers: "/partners/suppliers",
  materials: "/partners/materials",
  rfi: "/partners/rfi",
  rfq: "/partners/rfq",
  "rfq-requests": "/partners/rfq-requests",
  procurement: "/partners/procurement",
  performance: "/partners/performance",
  risk: "/partners/risk",
  regulatory: "/partners/regulatory",
  // Dossiers bare paths
  dossier: "/dossiers/dossier",
  pathways: "/dossiers/pathways",
};

/**
 * <LegacyRedirect> — preserves anything after the legacy slug so a stored
 * `/formulation-master/molecules/m1` bookmark still lands on
 * `/formulations/molecules/m1` (+ query string).
 */
function LegacyRedirect({ target }) {
  const { pathname, search, hash } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const rest = parts.slice(1).join("/"); // drop the legacy slug
  const to = rest ? `${target}/${rest}${search}${hash}` : `${target}${search}${hash}`;
  return <Navigate to={to} replace />;
}

/**
 * <NotFound> — replaces the silent Hub fallback for truly unknown paths.
 * Renders inside the Unifier shell (so the user is still authenticated) and
 * gives a clear signpost back to the merged app. Uses editorial house style.
 */
function NotFound() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6" data-testid="unifier-not-found">
      <div className="max-w-md text-center">
        <div className="font-mono-meta text-orange-600 mb-3">404 · UNKNOWN ROUTE</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-4">
          We couldn&apos;t find <em className="italic">that page</em>.
        </h1>
        <p className="text-foreground/70 mb-1">
          No page was found at <code className="font-mono text-sm">{pathname}</code> in Chandan.
        </p>
        <p className="text-muted-foreground text-sm mb-8">
          Use My work to open the module you need.
        </p>
        <Link to="/" className="btn-primary" data-testid="not-found-return-hub">
          Go to My work
        </Link>
      </div>
    </div>
  );
}

// The Unifier's shell (Hub + login) uses the shared AuthProvider.
// The Partners module is mounted under /partners/* and manages its own auth.
function UnifierShell() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Hub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <NotFound />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<div role="status" className="p-8">Loading module…</div>}>
        <Routes>
          {/* Partners module — has its own AuthProvider and login. */}
          <Route path="/bd/*" element={<BDModule />} />
          <Route path="/hrms/*" element={<HRMSModule />} />
          <Route path="/crm/*" element={<CRMModule />} />
          <Route path="/portfolio/*" element={<PortfolioModule />} />
          <Route path="/partners/*" element={<PartnersModule />} />
          {/* Formulation Master module — has its own AuthProvider. */}
          <Route path="/formulations/*" element={<FormulationsModule />} />
          {/* Dossier Assistant module — has its own AuthProvider. */}
          <Route path="/dossiers/*" element={<DossiersModule />} />
          <Route path="/clinical-intelligence/*" element={<ClinicalIntelligenceModule />} />
          {/* Legacy slug redirects — old bookmarks still land on the merged app. */}
          {Object.entries(LEGACY_REDIRECTS).map(([slug, target]) => (
            <Route
              key={slug}
              path={`/${slug}/*`}
              element={<LegacyRedirect target={target} />}
            />
          ))}
          {/* Bare-path standalone-era redirects (see BARE_LEGACY_REDIRECTS). */}
          {Object.entries(BARE_LEGACY_REDIRECTS).map(([slug, target]) => (
            <Route
              key={`bare-${slug}`}
              path={`/${slug}/*`}
              element={<LegacyRedirect target={target} />}
            />
          ))}
          {/* Everything else is the Unifier shell (Hub + login + 404). */}
          <Route path="/*" element={<UnifierShell />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
